<template>
  <div class="session-list">
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th scope="col">
            {{ $t('dashboard.volunteerDashboard.sessionList.header-1') }}
          </th>
          <th scope="col">
            {{ $t('dashboard.volunteerDashboard.sessionList.header-2') }}
          </th>
          <th scope="col">
            {{ $t('dashboard.volunteerDashboard.sessionList.header-3') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(session, index) in sortedOpenSessions"
          :key="`session-${index}`"
          class="session-row"
          @click="gotoSession(session)"
        >
          <td>{{ session.student.firstname }}</td>
          <td>
            {{ session.subjectDisplayName }}
          </td>
          <td>
            {{ waitTime(session.createdAt) }}
          </td>
        </tr>
      </tbody>
    </table>
    <audio
      class="audio__new-waiting-student"
      src="@/assets/audio/alert.mp3"
      muted
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import sendWebNotification from '@/utils/send-web-notification'
import Case from 'case'
import i18n from '@/i18n'

const VISIBLE_OPEN_SESSIONS_POLL_INTERVAL_MS = 1000 * 2
const HIDDEN_OPEN_SESSIONS_POLL_INTERVAL_MS = 1000 * 10

export default {
  data() {
    return {
      openSessions: [],
      emitListIntervalId: null,
      isInitialMount: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      isWebPageHidden: state => state.app.isWebPageHidden,
      sortedOpenSessions() {
        // sorts the sessions by createdAt, with oldest sessions coming first
        return this.openSessions.slice().sort((first, second) => {
          if (first.createdAt < second.createdAt) {
            return -1
          } else if (first.createdAt > second.createdAt) {
            return 1
          } else {
            return 0
          }
        })
      },
    }),
    ...mapGetters({
      isMutedSubjectAlertsActive: 'featureFlags/isMutedSubjectAlertsActive',
      isRecapSocketUpdatesActive: 'featureFlags/isRecapSocketUpdatesActive',
      dir: 'app/direction',
    }),
  },
  watch: {
    isWebPageHidden(isHidden) {
      if (this.emitListIntervalId) {
        this.startPollingOpenSessions()
      }

      if (!isHidden) {
        this.requestOpenSessions()
        this.$forceUpdate()
      }
    },
  },
  mounted() {
    this.isInitialMount = true
    if (this.isRecapSocketUpdatesActive || this.$socket.connected)
      this.emitList()
    else if (!this.$socket.connected) {
      this.$socket.connect()
      this.$socket.on('connect', this.emitList)
    }
  },
  beforeDestroy() {
    this.clearPollingInterval()
    this.$socket.off('connect', this.emitList)
  },
  methods: {
    getPollIntervalMs() {
      return this.isWebPageHidden
        ? HIDDEN_OPEN_SESSIONS_POLL_INTERVAL_MS
        : VISIBLE_OPEN_SESSIONS_POLL_INTERVAL_MS
    },
    clearPollingInterval() {
      if (!this.emitListIntervalId) return
      clearInterval(this.emitListIntervalId)
      this.emitListIntervalId = null
    },
    requestOpenSessions() {
      if (!this.$socket.connected) return
      this.$socket.emit('list')
    },
    emitList() {
      this.requestOpenSessions()
      this.startPollingOpenSessions()
    },
    gotoSession(session) {
      const { type, subTopic, _id } = session
      const path = `/${i18n.locale}/session/${Case.kebab(type)}/${Case.kebab(
        subTopic
      )}/${_id}`

      if (type && subTopic && _id) {
        this.$router.push(path)
      } else {
        this.$store.dispatch('user/clearSession')
      }
    },
    waitTime(time) {
      const newTime = new Date().getTime() - new Date(time).getTime()
      const seconds = Number((newTime / 1000).toFixed(0))
      const minutes = Number((newTime / (1000 * 60)).toFixed(0))
      const hours = Number((newTime / (1000 * 60 * 60)).toFixed(0))

      if (seconds < 60) {
        return '< 1 min'
      }
      if (minutes < 60) {
        if (minutes === 1) return `${minutes} min`
        return `${minutes} mins`
      }
      if (hours < 24) {
        if (hours === 1) return `${hours} hr`
        return `${hours} hrs`
      }
    },
    startPollingOpenSessions() {
      this.clearPollingInterval()
      this.emitListIntervalId = setInterval(() => {
        this.requestOpenSessions()
        this.$forceUpdate()
      }, this.getPollIntervalMs())
    },
  },
  sockets: {
    async sessions(sessions) {
      if (this.user.isBanned) {
        this.openSessions = []
        this.isInitialMount = false
        return
      }

      const results = []
      const socketSessions = sessions.filter(session => !session.volunteer)

      for (let i = 0; i < socketSessions.length; i++) {
        const session = socketSessions[i]
        const { subTopic } = session

        const isAdminOrTestUser = this.user.isAdmin || this.user.isTestUser
        // Show test accounts to admin and test volunteer accounts
        if (session.student.isTestUser && !isAdminOrTestUser) {
          continue
        }
        if (
          this.user.subjects.includes(subTopic) &&
          !(
            this.isMutedSubjectAlertsActive &&
            this.user.mutedSubjectAlerts.includes(subTopic)
          )
        ) {
          results.push(session)
        }
      }

      const prevOpenSessions = this.openSessions
      this.openSessions = results

      if (!this.isInitialMount) {
        // Look for the new session added
        let newSession
        for (const session of this.openSessions) {
          const { _id: sessionId } = session
          let isOldSession = false
          for (const oldSession of prevOpenSessions) {
            if (oldSession._id === sessionId) isOldSession = true
          }

          if (!isOldSession) newSession = session
        }

        if (newSession) {
          try {
            const newWaitingStudentAudio = document.querySelector(
              '.audio__new-waiting-student'
            )
            // Unmuting the audio allows us to bypass the need for user interaction with the DOM before playing a sound
            newWaitingStudentAudio.muted = false
            await newWaitingStudentAudio.play()
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log('Unable to play audio')
          }

          sendWebNotification(
            this.dir == 'ltr'
              ? `${newSession.student.firstname} needs help in ${newSession.subjectDisplayName}`
              : `${newSession.student.firstname} آیا نیاز به کمک در ${newSession.subjectDisplayName} داری؟`,
            {
              body: this.$root.$t(
                'dashboard.volunteerDashboard.sessionList.webNotificationBody'
              ),
            }
          )
        }
      }
      this.isInitialMount = false
    },
  },
}
</script>

<style lang="scss" scoped>
thead {
  text-align: center;
}

.session-list {
  padding: 10px 20px;
}

.session-row {
  cursor: pointer;
}

.session-row td {
  text-align: left;
}

.audio__new-waiting-student {
  display: none;
}

html.is-rtl {
  .session-row td {
    text-align: right;
  }
}
</style>
